import { Component, JSX, Signal } from "solid-js";

export type InferProps<T> = T extends Component<infer Props> ? Props : never

export interface StoryMetadata<T extends Component<any> = Component<any>> {
    component: T
    props: Partial<{
        [K in keyof InferProps<T>]: StoryParameterDefinition<InferProps<T>[K]>
    }>
}

export type StoryParameterDefinition<T> = {
    type: T extends Signal<infer U> ? LiteralType<U> : LiteralType<T>
    defaultValue?: T extends Signal<infer U> ? U : T
    options?: T extends Signal<infer U> ? 
        U extends string | number | symbol ? Record<U, string> : never
        : T extends string | number | symbol ? Record<T, string> : never
} & (
    T extends Signal<any> ? { isSignal: true } : { isSignal?: false }
)

export interface StoryDefinition<T extends Component<any> = Component<any>> {
    render?: (props: InferProps<T>) => JSX.Element
    props?: Partial<InferProps<T>>
    play?: (playProps: { snapshot: (name: string) => void }) => Promise<void>,
}

export const defineMetadata = <T extends Component<any>>(metadata: () => StoryMetadata<T>): () => StoryMetadata<T> => metadata

export const defineStory = <T extends Component<any>>(component: T, story: () => StoryDefinition<T>) => {
    return story;
}

export const registerStory = <T extends Component<any>>(name: string, metadata: () => StoryMetadata<T>, stories: Record<string, () => StoryDefinition<T>>) => {
    const componentDef = window.solidComponents[name];
    if (!componentDef) {
        throw new Error(`Component ${name} not found`);
    }

    componentDef.metadata = metadata;
    componentDef.stories = stories;
}

type LiteralType<T> = T extends string
    ? 'string'
    : T extends number
    ? 'number'
    : T extends boolean
    ? 'boolean'
    : T extends Function
    ? 'function'
    : T extends object
    ? 'object'
    : 'object'